import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { SITE_NAME } from '../../constants/seo'

/**
 * Updates document title, meta description, canonical URL, and basic Open Graph / Twitter tags.
 * Set REACT_APP_SITE_URL in production (e.g. https://thefunctionalnursepractitioner.com) for correct absolute URLs.
 */
function Seo({ title, description, noIndex = false }) {
  const location = useLocation()

  useEffect(() => {
    const base =
      (typeof process !== 'undefined' && process.env.REACT_APP_SITE_URL?.replace(/\/$/, '')) ||
      (typeof window !== 'undefined' ? window.location.origin : '')
    const path = `${location.pathname}${location.search || ''}`
    const url = `${base}${path}`
    const pageTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`

    document.title = pageTitle

    const ensureMeta = (selector, create) => {
      let el = document.head.querySelector(selector)
      if (!el) {
        el = create()
        document.head.appendChild(el)
      }
      return el
    }

    ensureMeta('meta[name="description"]', () => {
      const m = document.createElement('meta')
      m.setAttribute('name', 'description')
      return m
    }).setAttribute('content', description)

    const setOg = (property, content) => {
      ensureMeta(`meta[property="${property}"]`, () => {
        const m = document.createElement('meta')
        m.setAttribute('property', property)
        return m
      }).setAttribute('content', content)
    }

    const setName = (name, content) => {
      ensureMeta(`meta[name="${name}"]`, () => {
        const m = document.createElement('meta')
        m.setAttribute('name', name)
        return m
      }).setAttribute('content', content)
    }

    setOg('og:title', pageTitle)
    setOg('og:description', description)
    setOg('og:url', url)
    setOg('og:type', 'website')
    const imageUrl = `${base}/logo512.png`
    setOg('og:image', imageUrl)
    setOg('og:site_name', SITE_NAME)
    setOg('og:locale', 'en_US')

    setName('twitter:card', 'summary_large_image')
    setName('twitter:title', pageTitle)
    setName('twitter:description', description)
    setName('twitter:image', imageUrl)

    let canonical = document.head.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', url)

    if (noIndex) {
      setName('robots', 'noindex, nofollow')
    } else {
      const robots = document.head.querySelector('meta[name="robots"]')
      if (robots?.getAttribute('content') === 'noindex, nofollow') {
        robots.remove()
      }
    }
  }, [title, description, noIndex, location.pathname, location.search])

  return null
}

export default Seo
